import { GalleryCarousel } from '@greystar/designsystem';
import {
  getDictionaryEntry,
  PROPERTY_MAP_FALLBACK_IMAGE_BANNER_URL,
  PropertyImage,
  useAnalytics,
} from '@greystar/lib';
import React, { useMemo, JSX } from 'react';
import { PropertyImageCarouselProps } from '../../../component-props';
import ResponsiveImage from '../../ResponsiveImage/ResponsiveImage';
import VideoPlayer, { WistiaPlayerCustomElement } from '../../VideoPlayer/VideoPlayer';
import { useI18n } from 'next-localization';

const transformImages = (images: PropertyImage[]): React.ReactElement[] => {
  return images.map((image, idx) => (
    <ResponsiveImage
      key={`${idx}-${image.url}`}
      image={image.url}
      width={1440}
      height={617}
      sizes="(max-width: 768px) 100vw, 1200px"
      alt={image.alt ?? ''}
      fallbackImageSrc={PROPERTY_MAP_FALLBACK_IMAGE_BANNER_URL}
      quality={80}
      priority={idx === 0}
      loading={idx === 0 ? 'eager' : 'lazy'}
    />
  ));
};

type EnhancedPropertyImageCarouselProps = PropertyImageCarouselProps & {
  state?: string;
  propertyLocation?: string;
  propertyName?: string;
  propertyId?: string;
  propertyUrl?: string;
};

const PropertyImageCarousel = (props: EnhancedPropertyImageCarouselProps): JSX.Element => {
  const { images, videos, propertyName, state, propertyLocation, propertyUrl, propertyId } = props;
  const videoRefs = React.useRef<
    Record<string, React.MutableRefObject<WistiaPlayerCustomElement | null>>
  >({});
  const analytics = useAnalytics();
  const { t } = useI18n();
  useCreateRefsForEachVideo({ videoRefs, videos });

  const assignWistiaElementToVideoRef = (videoId: string, el: WistiaPlayerCustomElement): void => {
    if (!videoRefs.current[videoId]) {
      videoRefs.current[videoId] = { current: el };
    } else {
      videoRefs.current[videoId].current = el;
    }
  };

  const galleryImages = useMemo(() => {
    const transformedImages = transformImages(images ?? []);

    if (videos && videos.length > 0) {
      const videoElements = videos.map((video) => (
        <VideoPlayer
          key={video.videoId}
          mediaId={video.videoId}
          onPlayerInit={(el) => assignWistiaElementToVideoRef(video.videoId, el)}
        />
      ));

      return [transformedImages[0], ...videoElements, ...transformedImages.slice(1)];
    }

    return transformedImages;
  }, [images, videos]);

  const handleSlideChange = React.useCallback(() => {
    videos?.forEach(({ videoId }) => {
      videoRefs.current[videoId]?.current?.pause?.();
    });
  }, [videos]);

  const handleNavigate = (actionType: 'left' | 'right') => {
    const eventData = {
      event: 'carousel',
      action_type: actionType,
      state: state ?? '',
      property_location: propertyLocation ?? '',
      property_id: propertyId ?? '',
      property_name: propertyName ?? '',
      module_name: 'image viewer',
      inpage_location: 'top',
      click_text: actionType === 'left' ? 'Previous' : 'Next',
      click_type: 'internal',
      destination_url: propertyUrl,
    };

    analytics.trackEvent('carousel', eventData);
  };

  const pushPaginationAnalytics = (index: number) => {
    const dataEvent = {
      event: 'carousel',
      action_type: 'navigation indicator',
      state: state ?? '',
      property_location: propertyLocation ?? '',
      property_id: propertyId ?? '',
      property_name: propertyName ?? '',
      module_name: 'image viewer',
      inpage_location: 'middle',
      click_text: `Pagination Dot ${index + 1}`,
      click_type: 'internal',
      destination_url: propertyUrl,
    };

    analytics.trackEvent('carousel', dataEvent);
  };

  return (
    <GalleryCarousel
      images={galleryImages}
      onSlideChange={handleSlideChange}
      fractionText="/"
      onNavigateLeft={() => handleNavigate('left')}
      onNavigateRight={() => handleNavigate('right')}
      onPipClick={pushPaginationAnalytics}
      prevLabel={getDictionaryEntry(t('carousel-image-previous'), 'Gallery Image Previous')}
      nextLabel={getDictionaryEntry(t('carousel-image-next'), 'Gallery Image Next')}
    />
  );
};

export default PropertyImageCarousel;

function useCreateRefsForEachVideo({
  videos,
  videoRefs,
}: {
  videos: EnhancedPropertyImageCarouselProps['videos'];
  videoRefs: React.MutableRefObject<
    Record<string, React.MutableRefObject<WistiaPlayerCustomElement | null>>
  >;
}) {
  React.useEffect(() => {
    if (videos) {
      videos.forEach((video) => {
        if (!videoRefs.current[video.videoId]) {
          videoRefs.current[video.videoId] = { current: null };
        }
      });
    }
  }, [videos, videoRefs]);
}
